/**
 * Client-Side Health Reporter — collects POS health metrics and reports.
 *
 * Metrics: error count, freeze count, API latency, sync success rate.
 * Reports to CW Browser observability API (Electron) or console (web).
 *
 * Health score: 0-100, based on weighted components.
 */

import { recordRequestOutcome } from "./requestFailure";

const REPORT_INTERVAL = 5 * 60 * 1000; // 5 minutes

let _metrics = {
  errors: 0,
  freezes: 0,
  apiCalls: 0,
  apiErrors: 0,
  apiTotalLatency: 0,
  syncAttempts: 0,
  syncFailures: 0,
  lastReportedAt: Date.now(),
};

let _reportTimer = null;

// ── Track Events ──

export function trackError() { _metrics.errors++; }
export function trackFreeze() { _metrics.freezes++; }
export function trackApiCall(latencyMs, success = true) {
  _metrics.apiCalls++;
  _metrics.apiTotalLatency += latencyMs;
  if (!success) _metrics.apiErrors++;
}
export function trackSync(success = true) {
  _metrics.syncAttempts++;
  if (!success) _metrics.syncFailures++;
}

// ── Health Score ──

export function computeHealthScore() {
  const m = _metrics;
  let score = 100;

  // Errors: -5 per error, max -30
  score -= Math.min(m.errors * 5, 30);

  // Freezes: -15 per freeze, max -30
  score -= Math.min(m.freezes * 15, 30);

  // API error rate: -20 if >10% failures
  if (m.apiCalls > 5) {
    const errorRate = m.apiErrors / m.apiCalls;
    if (errorRate > 0.1) score -= 20;
    else if (errorRate > 0.05) score -= 10;
  }

  // Sync failure rate: -20 if >5% failures
  if (m.syncAttempts > 0) {
    const failRate = m.syncFailures / m.syncAttempts;
    if (failRate > 0.05) score -= 20;
  }

  // API latency: -10 if avg >5s
  if (m.apiCalls > 0) {
    const avgLatency = m.apiTotalLatency / m.apiCalls;
    if (avgLatency > 5000) score -= 10;
    else if (avgLatency > 3000) score -= 5;
  }

  return Math.max(0, Math.min(100, score));
}

// ── Report ──

function report() {
  const score = computeHealthScore();
  const avgLatency = _metrics.apiCalls > 0 ? Math.round(_metrics.apiTotalLatency / _metrics.apiCalls) : 0;

  const payload = {
    healthScore: score,
    errors: _metrics.errors,
    freezes: _metrics.freezes,
    apiCalls: _metrics.apiCalls,
    apiErrors: _metrics.apiErrors,
    avgApiLatencyMs: avgLatency,
    syncAttempts: _metrics.syncAttempts,
    syncFailures: _metrics.syncFailures,
    reportedAt: new Date().toISOString(),
  };

  // Report to CW Browser observability
  if (window.electronAPI?.observability?.log) {
    window.electronAPI.observability.log("info", "health", `Score: ${score}`, payload);
  }

  // Reset counters for next period
  _metrics = {
    errors: 0,
    freezes: 0,
    apiCalls: 0,
    apiErrors: 0,
    apiTotalLatency: 0,
    syncAttempts: 0,
    syncFailures: 0,
    lastReportedAt: Date.now(),
  };
}

// ── Lifecycle ──

export function startHealthReporter() {
  if (_reportTimer) return;
  _reportTimer = setInterval(report, REPORT_INTERVAL);
}

// ── Axios Integration ──

/**
 * Attach request/response interceptors to an axios instance to
 * automatically track API call latency and success/failure.
 * Safe to call multiple times on different instances.
 */
export function applyHealthInterceptors(instance) {
  instance.interceptors.request.use((config) => {
    config._healthStartTime = Date.now();
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      const latency = Date.now() - (response.config._healthStartTime || Date.now());
      trackApiCall(latency, true);
      recordRequestOutcome(response.config);
      return response;
    },
    (error) => {
      const latency = Date.now() - (error.config?._healthStartTime || Date.now());
      trackApiCall(latency, false);
      recordRequestOutcome(error.config, error);
      return Promise.reject(error);
    }
  );
}
